import moment from 'moment';
import { useMemo } from 'react';
import { useLingui } from '@lingui/react';

import { AUTO_LANGUAGE, DEFAULT_LANGUAGE, isLanguageCode } from '@config';
import { messages } from '@locales';

import { useActiveOrganization, useUserState } from '@hooks';

import { LocalizedMoment } from '@types';

/**
 * The locale actually rendering right now, read back from lingui.
 *
 * Use this for formatting (dates, `Intl.*`). To decide what *should* render,
 * see `useEffectiveLanguage` — during the first paint after an organization
 * switch the two differ for one frame, because lingui loads its catalogue
 * asynchronously.
 */
export const useLanguage = (): LanguageCode => {
  const { i18n } = useLingui();
  return i18n.locale as LanguageCode;
};

/** The active organization's language, or Czech when there is no organization. */
export const useOrganizationLanguage = (): LanguageCode => {
  const activeOrganization = useActiveOrganization();
  const language = (activeOrganization as Organization | null)?.language;
  return isLanguageCode(language) ? language : DEFAULT_LANGUAGE;
};

/**
 * Which locale the app should be in: the profile pin when there is one,
 * otherwise the active organization's language.
 *
 * This is the one place the two settings meet. A user who never touches their
 * profile follows whichever organization they switch to; a user who picked a
 * language keeps it across every organization, which is the whole reason the
 * profile control still exists.
 */
export const useEffectiveLanguage = (): LanguageCode => {
  const { languagePreference } = useUserState();
  const organizationLanguage = useOrganizationLanguage();
  return languagePreference === AUTO_LANGUAGE
    ? organizationLanguage
    : languagePreference;
};

// Returns localized callable moment
export const useMoment = (): LocalizedMoment => {
  const language = useLanguage();
  const localizedMoment: LocalizedMoment = (...args) => {
    return moment(...args).locale(language) as moment.Moment;
  };
  localizedMoment.locale = language;
  return localizedMoment;
};

// This is used to return the "usual" date format
// for the current locale, e.g. MM/DD/YYYY or DD.MM.YYYY
export const useStandardDateFormat = () => {
  const moment = useMoment();
  return useMemo(() => {
    return moment().localeData().longDateFormat('L');
  }, [moment]);
};

// Returns a function that allows messages
// to be retrieved from '@locales/messages.ts' by key,
export const useGetTranslatedMessage = () => {
  const { _ } = useLingui();

  return <T extends keyof typeof messages>(
    key: T,
    ...args: Parameters<(typeof messages)[T]>
  ) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return _(messages[key](...args));
  };
};
